import React from 'react';
import { motion } from 'framer-motion';
import { VerificationResult } from '../../types/verification';
import { AlertCircle, CheckCircle, Package, Plane } from 'lucide-react';

type Props = {
  results: VerificationResult[];
};

export const VerificationResults = ({ results }: Props) => {
  const hasIssues = results.some(result => !result.isValid);

  if (!hasIssues) {
    return (
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-green-50 p-6 rounded-lg"
      >
        <div className="flex items-center gap-4">
          <div className="p-3 bg-green-100 rounded-full">
            <CheckCircle className="text-green-500" size={24} />
          </div>
          <div>
            <h3 className="text-lg font-medium text-green-700">
              جميع البيانات متطابقة
            </h3>
            <p className="text-green-600 mt-1">
              تم التحقق من جميع البيانات وتأكيد ترابطها بشكل صحيح
            </p>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-white rounded-lg shadow-sm overflow-hidden border"
    >
      <div className="p-4 bg-red-50 border-b border-red-100">
        <div className="flex items-center gap-3">
          <AlertCircle className="text-red-500" size={24} />
          <div>
            <h3 className="font-medium text-red-700">تم العثور على مشاكل في الترابط</h3>
            <p className="text-red-600 text-sm mt-1">
              يرجى مراجعة المشاكل التالية وتصحيحها
            </p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {results.filter(result => !result.isValid).map((result, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.1 }}
            className="p-4 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start gap-4">
              <div className={`p-2 rounded-lg shrink-0 ${
                result.type === 'package' ? 'bg-blue-50' : 'bg-yellow-50'
              }`}>
                {result.type === 'package' ? (
                  <Package className="text-blue-500" size={20} />
                ) : (
                  <Plane className="text-yellow-500" size={20} />
                )}
              </div>
              
              <div className="flex-1 space-y-1">
                <div className="font-medium">{result.pilgrimName}</div>
                <div className="text-sm text-gray-600">
                  {result.type === 'pnr' ? (
                    <>
                      <span className="font-medium">رقم PNR: </span>
                      {result.value}
                    </>
                  ) : (
                    <>
                      <span className="font-medium">اسم الباقة: </span>
                      {result.value}
                    </>
                  )}
                </div>
                <div className="text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle size={14} />
                  <span>{result.message}</span>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};